import React, { useMemo } from 'react';
import { useI18n } from '../../context/I18nContext';
import HybridCableVisualization from './HybridCableVisualization';
import CableTessera from './CableTessera';
import { getCableDisplayName, getMultipolarCablesDisplayNames } from '../../utils/cableLogic';
import { getGroupDisplayTitle } from '../../utils/groups';

const SchematicNestedVisualization = ({
  nodes = [],
  settings,
  availableSheaths = [],
  isMobile,
  localHoveredCable,
  setLocalHoveredCable,
  editCable,
  editMultipolarCable,
  deleteCable,
  deleteMultipolarCable,
  validateCable,
  editingIndex,
  editingMultipolarIndex,
  showDeleteButtons,
  toggleDeleteButton,
  isSelectionModeEnabled,
  selectedCables
}) => {
  const { t } = useI18n();
  const renderOnlyTopLevel = true;

  const nodesToRender = useMemo(() => {
    const filtered = renderOnlyTopLevel
      ? (nodes || []).filter(node => (node.level ?? 1) === 1)
      : nodes || [];

    return filtered;
  }, [nodes, renderOnlyTopLevel]);

  const appendSubgroupInfo = (baseTitle, node) => {
    if (node?.subgroupIndex && node?.totalSubgroups && node.totalSubgroups > 1) {
      return `${baseTitle} (${node.subgroupIndex}/${node.totalSubgroups})`;
    }
    return baseTitle;
  };

  const getNodeTitle = (node) => {
    if (node.type === 'group') {
      const groupCode = node.groupCode || node.id;
      const baseTitle = getGroupDisplayTitle(groupCode, settings.groupTitles);
      return appendSubgroupInfo(baseTitle, node);
    }

    if (node.type === 'sheath' && node.sheathId) {
      const sheath = availableSheaths?.find(s => s.id === node.sheathId);
      const baseLabel = sheath
        ? (sheath.fullLabel || `${t('schematic.sheathLabel')} ${sheath.label}`)
        : `${t('schematic.sheathLabel')} ${node.sheathId}`;
      return appendSubgroupInfo(baseLabel, node);
    }

    const firstCable = node.cables?.[0];
    if (!firstCable) return node.label || t('schematic.cable');

    if (settings?.marcaturePerCollegamento) {
      const cableNumbers = node.cables
        ?.map(cable => `#${(cable.originalIndex ?? 0) + 1}`)
        .join(', ');
      const numbersLabel = (node.cables?.length || 0) > 1 ? t('schematic.cablesLabel') : t('schematic.cableLabel');
      return `${numbersLabel} ${cableNumbers}`;
    }

    // Un gruppo può contenere più cavi multipolari: elencali tutti nel titolo.
    const multipolarNames = getMultipolarCablesDisplayNames(node.cables, settings.lingua);
    const displaySiglatura = multipolarNames.length > 0
      ? multipolarNames.join(', ')
      : getCableDisplayName(firstCable, settings.lingua, settings);
    const cableLabel = multipolarNames.length > 1 ? t('schematic.cablesLabel') : t('schematic.cableLabel');
    const baseCableTitle = `${cableLabel} ${displaySiglatura}`;
    return appendSubgroupInfo(baseCableTitle, node);
  };

  const renderNode = (node, nestingLevel = 0) => {
    const isSheath = node.type === 'sheath';
    const isGroup = node.type === 'group';
    const isMultiple = node.type === 'multiple';

    const badgeText = isSheath
      ? t('schematic.sheath')
      : isMultiple
        ? t('schematic.multiple')
        : isGroup && (node.groupCode || node.id)
          ? `${t('schematic.groupLabel')} ${node.groupCode || node.id}`
          : null;

    const infoText = (() => {
      const count = node.cables?.length || 0;
      if (count === 0) {
        return t('schematic.noCablesInserted');
      }
      if (count === 1) {
        return t('schematic.singleCable');
      }
      return `${count} ${t('schematic.cables')}`;
    })();

    const title = getNodeTitle(node);

    return (
      <div
        key={node.id}
        data-nested-node-id={node.id}
        data-node-type={node.type}
        data-group-code={isGroup ? (node.groupCode || node.id) : undefined}
        data-subgroup-index={node.subgroupIndex || undefined}
        data-total-subgroups={node.totalSubgroups || undefined}
        className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm space-y-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <h3 className="text-lg font-semibold text-gray-800">
              {title}
            </h3>
            <p className="text-sm text-gray-600">{infoText}</p>
          </div>
          {badgeText && (
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isSheath
              ? 'bg-sky-100 text-sky-800'
              : isMultiple
                ? 'bg-purple-100 text-purple-800'
                : isGroup
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
              {badgeText}
            </div>
          )}
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <HybridCableVisualization
            cables={node.cables || []}
            settings={settings}
            siglatura={node.groupCode || node.id}
            hoveredCable={localHoveredCable}
            setHoveredCable={setLocalHoveredCable}
            isMobile={isMobile}
            availableSheaths={availableSheaths}
            processedSubGroups={isSheath ? node.subGroups : null}
            forcedSheathId={isSheath ? node.sheathId : null}
          />
        </div>

        {!isMobile && (
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="space-y-2">
              {(node.cables || []).map((cable, idx) => {
                const isHovered = localHoveredCable === cable.originalIndex;
                const displaySiglatura = getCableDisplayName(cable, settings.lingua, settings);

                return (
                  <div
                    key={`${cable.originalIndex}-${idx}`}
                    className="transition-all duration-200"
                    onMouseEnter={() => setLocalHoveredCable(cable.originalIndex)}
                    onMouseLeave={() => setLocalHoveredCable(null)}
                  >
                    <CableTessera
                      cable={cable}
                      displaySiglatura={displaySiglatura}
                      onCableClick={null}
                      onEditCable={editCable}
                      onEditMultipolarCable={editMultipolarCable}
                      onDeleteCable={deleteCable}
                      onDeleteMultipolarCable={deleteMultipolarCable}
                      showSiglatura={true}
                      clickable={false}
                      settings={settings}
                      validateCable={validateCable}
                      isHovered={isHovered}
                      editingIndex={editingIndex}
                      editingMultipolarIndex={editingMultipolarIndex}
                      showDeleteButton={showDeleteButtons.has(cable.originalIndex)}
                      onToggleDelete={() => toggleDeleteButton(cable.originalIndex)}
                      isSelectionModeEnabled={isSelectionModeEnabled}
                      selectedCables={selectedCables}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {node.subGroups && node.subGroups.length > 0 && !renderOnlyTopLevel && (
          <div className="pl-4 border-l-2 border-gray-200 space-y-4">
            {node.subGroups.map((child) => renderNode(child, nestingLevel + 1))}
          </div>
        )}

      </div>
    );
  };

  return (
    <div className="space-y-8">
      {nodesToRender.map((node) => renderNode(node))}
    </div>
  );
};

export default SchematicNestedVisualization;
